import { z } from "zod";
import { ceilWonAmount } from "../workspace/model";

export const TRANSACTION_REVIEW_STATUSES = ["NEW", "RESUBMITTED"] as const;
export const TRANSACTION_REASON_MAX_LENGTH = 2_000;
export const TRANSACTION_SUPPLEMENT_REASON_MIN_LENGTH = 5;

const isoDatePattern = /^(\d{4})-(\d{2})-(\d{2})$/;

export function isValidTransactionDate(value: string): boolean {
  if (!value) return true;
  const match = isoDatePattern.exec(value);
  if (!match) return false;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (year < 1900 || year > 9998) return false;
  const date = new Date(Date.UTC(year, month - 1, day));
  return date.getUTCFullYear() === year
    && date.getUTCMonth() === month - 1
    && date.getUTCDate() === day;
}

export const transactionSearchFormSchema = z.object({
  status: z.union([z.literal(""), z.enum(TRANSACTION_REVIEW_STATUSES)]),
  reviewStatus: z.enum(["ALL", "PENDING", "APPROVED", "REJECTED"]),
  keyword: z.string().max(100, "검색어는 100자 이하로 입력해 주세요."),
  memberId: z.string().refine(
    (value) => {
      const normalized = value.trim();
      return normalized === "" || (/^\d+$/.test(normalized)
        && Number.isSafeInteger(Number(normalized))
        && Number(normalized) > 0);
    },
    "회원 ID는 1 이상의 숫자로 입력해 주세요.",
  ),
  from: z.string().refine(isValidTransactionDate, "유효한 접수 시작일을 선택해 주세요."),
  to: z.string().refine(isValidTransactionDate, "유효한 접수 종료일을 선택해 주세요."),
}).superRefine((values, context) => {
  if (values.from && values.to && values.from > values.to) {
    context.addIssue({
      code: "custom",
      message: "접수 종료일은 시작일보다 빠를 수 없습니다.",
      path: ["to"],
    });
  }
});

export type TransactionSearchFormValues = z.infer<typeof transactionSearchFormSchema>;

export function parseTransactionReviewStatus(
  value: string | null,
): TransactionSearchFormValues["status"] {
  if (value === "ALL") return "";
  const parsed = z.enum(TRANSACTION_REVIEW_STATUSES).safeParse(value);
  return parsed.success ? parsed.data : "";
}

export function parseTransactionMemberId(value: string | null): string {
  if (!value || !/^\d+$/.test(value)) return "";
  const parsed = Number(value);
  return Number.isSafeInteger(parsed) && parsed > 0 ? String(parsed) : "";
}

export function parseTransactionDate(value: string | null): string {
  return value && isValidTransactionDate(value) ? value : "";
}

export function parseApprovedTransactionAmount(value: string, requestedAmount: number): number | null {
  if (!/^\d+(\.\d{1,2})?$/.test(value.trim())) return null;
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  const amount = ceilWonAmount(parsed);
  return Number.isSafeInteger(amount) && amount <= requestedAmount ? amount : null;
}
